"use client"

import { useState, useEffect } from "react"
import "./OrderTracking.css"
import { getDatabase, ref, get } from "firebase/database" // Changed to Realtime Database
import { getAuth } from "firebase/auth"

export default function OrderTracking() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState(null)

  // Get current user
  const auth = getAuth()
  const database = getDatabase() // Use Realtime Database

  useEffect(() => {
    const fetchOrders = async () => {
      if (!auth.currentUser) {
        setLoading(false)
        return
      }

      try {
        // Get user's orders from Realtime Database
        const userOrdersRef = ref(database, `users/${auth.currentUser.uid}/orders`)
        const userOrdersSnapshot = await get(userOrdersRef)

        if (userOrdersSnapshot.exists()) {
          const orderIds = Object.keys(userOrdersSnapshot.val())

          const orderPromises = orderIds.map(async (orderId) => {
            const orderRef = ref(database, `orders/${orderId}`)
            const orderSnapshot = await get(orderRef)

            if (orderSnapshot.exists()) {
              const orderData = orderSnapshot.val()
              return {
                id: orderId,
                ...orderData,
                createdAt: new Date(orderData.createdAt),
              }
            }
            return null
          })

          const fetchedOrders = (await Promise.all(orderPromises))
            .filter((order) => order !== null)
            .sort((a, b) => b.createdAt - a.createdAt)

          setOrders(fetchedOrders)

          // Select the most recent order by default
          if (fetchedOrders.length > 0) {
            setSelectedOrder(fetchedOrders[0])
          }
        }
      } catch (error) {
        console.error("Error fetching orders:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [auth.currentUser, database])

  const getStatusPercentage = (status) => {
    const statuses = ["processing", "confirmed", "preparing", "ready", "completed"]
    const index = statuses.indexOf(status)
    if (index === -1) return 0
    return (index / (statuses.length - 1)) * 100
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (loading) {
    return <div className="loading">Loading your orders...</div>
  }

  if (orders.length === 0) {
    return (
      <div className="no-orders-container">
        <h1>No Orders Found</h1>
        <p>You don't have any orders yet. Start shopping to place your first order!</p>
        <button className="primary-button" onClick={() => (window.location.href = "/")}>
          Shop Now
        </button>
      </div>
    )
  }

  return (
    <div className="tracking-container">
      <div className="tracking-wrapper">
        <h1 className="tracking-title">Order Tracking</h1>

        <div className="tracking-layout">
          <div className="orders-list-container">
            <h2 className="section-title">Your Orders</h2>
            <div className="orders-list">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className={`order-item ${selectedOrder?.id === order.id ? "selected" : ""}`}
                  onClick={() => setSelectedOrder(order)}
                >
                  <div className="order-item-header">
                    <span className="order-number">#{order.orderNumber}</span>
                    <span className={`order-status status-${order.status}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </div>
                  <div className="order-item-details">
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                    <span className="order-total">₹{order.totals.total.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedOrder && (
            <div className="order-details-container">
              <div className="order-header">
                <h2 className="order-title">Order #{selectedOrder.orderNumber}</h2>
                <span className={`order-status status-${selectedOrder.status}`}>
                  {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                </span>
              </div>

              <div className="order-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${getStatusPercentage(selectedOrder.status)}%` }}
                  ></div>
                </div>
                <div className="progress-steps">
                  <div
                    className={`progress-step ${selectedOrder.status === "processing" || getStatusPercentage(selectedOrder.status) >= 0 ? "active" : ""}`}
                  >
                    <div className="step-circle"></div>
                    <span>Processing</span>
                  </div>
                  <div
                    className={`progress-step ${selectedOrder.status === "confirmed" || getStatusPercentage(selectedOrder.status) >= 25 ? "active" : ""}`}
                  >
                    <div className="step-circle"></div>
                    <span>Confirmed</span>
                  </div>
                  <div
                    className={`progress-step ${selectedOrder.status === "preparing" || getStatusPercentage(selectedOrder.status) >= 50 ? "active" : ""}`}
                  >
                    <div className="step-circle"></div>
                    <span>Preparing</span>
                  </div>
                  <div
                    className={`progress-step ${selectedOrder.status === "ready" || getStatusPercentage(selectedOrder.status) >= 75 ? "active" : ""}`}
                  >
                    <div className="step-circle"></div>
                    <span>Ready</span>
                  </div>
                  <div
                    className={`progress-step ${selectedOrder.status === "completed" || getStatusPercentage(selectedOrder.status) >= 100 ? "active" : ""}`}
                  >
                    <div className="step-circle"></div>
                    <span>Completed</span>
                  </div>
                </div>
              </div>

              <div className="order-info-grid">
                <div className="info-card">
                  <h3 className="info-title">Shipping Address</h3>
                  <div className="info-content">
                    <p>{selectedOrder.address.street}</p>
                    <p>
                      {selectedOrder.address.city}, {selectedOrder.address.state} {selectedOrder.address.zipCode}
                    </p>
                    <p>Phone: {selectedOrder.address.phone}</p>
                  </div>
                </div>

                <div className="info-card">
                  <h3 className="info-title">Order Information</h3>
                  <div className="info-content">
                    <div className="info-row">
                      <span className="info-label">Order Date:</span>
                      <span className="info-value">{formatDate(selectedOrder.createdAt)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Payment Method:</span>
                      <span className="info-value">Credit Card</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Total Amount:</span>
                      <span className="info-value">₹{selectedOrder.totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-items">
                <h3 className="info-title">Order Items</h3>
                <div className="items-list">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="item">
                      <div className="item-image">
                        <img src={item.image || "/placeholder.svg"} alt={item.name} />
                      </div>
                      <div className="item-info">
                        <h4 className="item-name">{item.name}</h4>
                        <p className="item-price">
                          ₹{item.price.toFixed(2)} × {item.quantity}
                        </p>
                      </div>
                      <div className="item-total">₹{(item.price * item.quantity).toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                <div className="order-summary">
                  <div className="summary-row">
                    <span className="summary-label">Subtotal</span>
                    <span className="summary-value">₹{selectedOrder.totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Tax</span>
                    <span className="summary-value">₹{selectedOrder.totals.tax.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Shipping</span>
                    <span className="summary-value">₹{selectedOrder.totals.shipping.toFixed(2)}</span>
                  </div>
                  <div className="summary-row total">
                    <span className="summary-label">Total</span>
                    <span className="summary-value">₹{selectedOrder.totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button className="support-button" onClick={() => (window.location.href = "/contact")}>
                  Contact Support
                </button>
                <button className="reorder-button" onClick={() => (window.location.href = "/")}>
                  Reorder
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

